import React from "react"
import { twMerge } from "tailwind-merge"
import { IMG_TYPES, cleanSrcSet, getFallbackSrc, catalogSrcSet } from "../utils/images"

const PLACEHOLDER = "/static/p-images/placeholder--150x150.jpg"

/**
 * Responsive <picture> for API images. A catalog image is the plain url of the
 * original: its srcset is built over the api's `?size=` buckets. CMS images
 * may still come as {crop: [webpSrcset, jpegSrcset]}. `sizes` lets the
 * browser pick the right srcset candidate.
 */
export default function Img({
  src,
  crop = "default",
  alt = "",
  width,
  height,
  sizes,
  className = "",
  imgClassName = "",
  fit = "cover",
  loading = "lazy",
  fetchPriority,
  style,
  onLoad,
}) {
  // Missing media (e.g. a CDN image absent locally) falls back to the placeholder
  const onError = (e) => {
    const img = e.currentTarget
    if (img.dataset.fallback) return
    img.dataset.fallback = "1"
    for (const source of img.parentElement?.querySelectorAll("source") || []) source.remove()
    img.srcset = ""
    img.src = PLACEHOLDER
  }
  const fitClass = fit === "contain" ? "object-contain" : fit === "none" ? "" : "object-cover"
  const imgCls = twMerge("block w-full h-full", fitClass, imgClassName)
  const numericWidth = typeof width === "number" ? width : width ? parseInt(width) : undefined
  const numericHeight = typeof height === "number" ? height : height ? parseInt(height) : undefined
  const resolvedSizes = sizes ?? (numericWidth ? `${numericWidth}px` : undefined)

  if (!src || typeof src === "string") {
    const srcSet = catalogSrcSet(src) || undefined
    return (
      <picture className={twMerge("block leading-none", className)} style={style}>
        <img
          src={src || PLACEHOLDER}
          srcSet={srcSet}
          sizes={srcSet ? resolvedSizes || "100vw" : undefined}
          alt={alt}
          width={numericWidth}
          height={numericHeight}
          loading={loading}
          fetchpriority={fetchPriority}
          className={imgCls}
          onLoad={onLoad}
          onError={onError}
        />
      </picture>
    )
  }

  const responsive = src[crop] || src.default
  if (!responsive?.length) {
    return (
      <picture className={twMerge("block leading-none", className)} style={style}>
        <img src={PLACEHOLDER} alt={alt} className={imgCls} loading={loading} />
      </picture>
    )
  }

  return (
    <picture className={twMerge("block leading-none", className)} style={style}>
      {responsive.map((srcset, i) => (
        <source key={i} srcSet={cleanSrcSet(srcset)} type={IMG_TYPES[i]} sizes={resolvedSizes} />
      ))}
      <img
        src={getFallbackSrc(src)}
        alt={alt}
        width={numericWidth}
        height={numericHeight}
        loading={loading}
        fetchpriority={fetchPriority}
        className={imgCls}
        onLoad={onLoad}
      />
    </picture>
  )
}
